'use client'

import { useCallback, useEffect, useLayoutEffect, useReducer, useRef } from 'react'

import type { ModelPanelData } from '@/components/server/product/panel-types'
import { createLogger } from '@/lib/log/client'

import type { TooltipData } from './feature-tooltip'

const log = createLogger('use-model-panels')

// useLayoutEffect on the client, useEffect on the server (avoids the SSR warning).
// The reveal must run before paint to prevent a brief double-visible flash.
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

const panelKey = (modelId: number, levelId: number | undefined) => `${modelId}-${levelId}`

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number
}

interface State {
  activeModelId: number
  activeLevelId: number | undefined
  expanded: boolean
  /** Fetched (non-server) panels, keyed `${modelId}-${levelId}`. */
  panels: Record<string, ModelPanelData>
  /** Merged tooltip map: the default panel's tooltips + every fetched panel's. */
  tooltips: Record<string, TooltipData>
  /** Key currently being awaited by an active selection (drives the skeleton). */
  loadingKey: string | null
}

type Action =
  | { type: 'SELECT_MODEL'; modelId: number; levelId: number | undefined }
  | { type: 'SELECT_LEVEL'; levelId: number }
  | { type: 'TOGGLE_FEATURES' }
  | { type: 'PANEL_LOADING'; key: string }
  | { type: 'PANEL_LOADED'; key: string; panel: ModelPanelData; tooltips: Record<string, TooltipData> }
  | { type: 'PANEL_ERROR'; key: string }

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SELECT_MODEL':
      return {
        ...state,
        activeModelId: action.modelId,
        activeLevelId: action.levelId,
        expanded: false,
        loadingKey: null,
      }
    case 'SELECT_LEVEL':
      return { ...state, activeLevelId: action.levelId, expanded: false, loadingKey: null }
    case 'TOGGLE_FEATURES':
      return { ...state, expanded: !state.expanded }
    case 'PANEL_LOADING':
      return { ...state, loadingKey: action.key }
    case 'PANEL_LOADED':
      return {
        ...state,
        panels: { ...state.panels, [action.key]: action.panel },
        // Idempotent merge — tooltip ids are content-hashed, so identical tooltips
        // across panels share an id and never collide.
        tooltips: { ...state.tooltips, ...action.tooltips },
        loadingKey: state.loadingKey === action.key ? null : state.loadingKey,
      }
    case 'PANEL_ERROR':
      return { ...state, loadingKey: state.loadingKey === action.key ? null : state.loadingKey }
    default:
      return state
  }
}

interface UseModelPanelsParams {
  releaseSlug: string
  locale: string
  levelsByModel: Record<number, number[]>
  defaultModelId: number
  defaultLevelId: number | undefined
  serverPanelKeys: string[]
  initialTooltips: Record<string, TooltipData>
}

export const useModelPanels = ({
  releaseSlug,
  locale,
  levelsByModel,
  defaultModelId,
  defaultLevelId,
  serverPanelKeys,
  initialTooltips,
}: UseModelPanelsParams) => {
  const panelsRef = useRef<HTMLDivElement>(null)
  const inFlight = useRef(new Set<string>())
  const serverKeys = useRef<Set<string> | null>(null)
  if (!serverKeys.current) {
    serverKeys.current = new Set(serverPanelKeys)
  }

  const [state, dispatch] = useReducer(reducer, {
    activeModelId: defaultModelId,
    activeLevelId: defaultLevelId,
    expanded: false,
    panels: {},
    tooltips: initialTooltips,
    loadingKey: null,
  })

  // A ref to the loaded panels so fetchPanel can check availability without
  // depending on `state` (keeps the callback stable).
  const loadedRef = useRef(state.panels)
  loadedRef.current = state.panels
  const stateRef = useRef(state)
  stateRef.current = state

  // Reveal the active panel once its node exists (server child or freshly
  // committed fetched panel). No-op when the target isn't ready, so the prior
  // panel stays visible during a cold fetch.
  const showPanel = useCallback((modelId: number, levelId: number | undefined) => {
    const container = panelsRef.current
    if (!container) {
      return
    }
    const active = container.querySelector<HTMLElement>(`[data-model-id="${modelId}"][data-level-id="${levelId}"]`)
    if (!active) {
      return
    }
    for (const el of container.querySelectorAll<HTMLElement>('[data-model-id]')) {
      el.style.display = 'none'
    }
    active.style.display = 'block'
    active.dispatchEvent(new CustomEvent('panel-visible', { bubbles: false }))
  }, [])

  // The default panel is server-rendered already visible, so skip the first run;
  // re-run whenever the active selection changes or a new panel commits.
  const didMount = useRef(false)
  const loadedSignature = Object.keys(state.panels).join(',')
  useIsoLayoutEffect(() => {
    if (!didMount.current) {
      didMount.current = true
      return
    }
    showPanel(state.activeModelId, state.activeLevelId)
  }, [showPanel, state.activeModelId, state.activeLevelId, loadedSignature])

  const fetchPanel = useCallback(
    async (modelId: number, levelId: number | undefined, active: boolean) => {
      const key = panelKey(modelId, levelId)
      if (serverKeys.current?.has(key) || loadedRef.current[key]) {
        return
      }
      if (active) {
        dispatch({ type: 'PANEL_LOADING', key })
      }
      if (inFlight.current.has(key)) {
        return
      }
      inFlight.current.add(key)
      try {
        const qs = new URLSearchParams({ model_id: String(modelId), locale })
        if (levelId != null) {
          qs.set('level_id', String(levelId))
        }
        const url = `/api/product/${encodeURIComponent(releaseSlug)}/panel?${qs.toString()}`
        let res = await fetch(url)
        if (!res.ok && res.status >= 500) {
          // Panel 500s are transient backend blips — a cold DB connection or
          // brief pool pressure (see the server-side 57014 fixes). Retry once
          // after a short delay: the connection is warm / the pool has freed by
          // then, so most recover and the user sees the panel instead of an
          // error state (HT-APP-61). A persisting failure still throws below.
          await new Promise((resolve) => setTimeout(resolve, 400))
          res = await fetch(url)
        }
        if (!res.ok) {
          throw new Error(`panel request failed: ${res.status}`)
        }
        const json = (await res.json()) as { panel: ModelPanelData; tooltips: Record<string, TooltipData> }
        dispatch({ type: 'PANEL_LOADED', key, panel: json.panel, tooltips: json.tooltips })
      } catch (err) {
        log.error('panel fetch failed', err)
        dispatch({ type: 'PANEL_ERROR', key })
      } finally {
        inFlight.current.delete(key)
      }
    },
    [locale, releaseSlug]
  )

  // The level shown when a given model is selected: keep the current level if it's
  // valid for that model, else its first valid level.
  const levelForModel = useCallback(
    (modelId: number): number | undefined => {
      const valid = levelsByModel[modelId] ?? []
      return valid.includes(stateRef.current.activeLevelId ?? -1) ? stateRef.current.activeLevelId : valid[0]
    },
    [levelsByModel]
  )

  const selectModel = useCallback(
    (modelId: number) => {
      const levelId = levelForModel(modelId)
      dispatch({ type: 'SELECT_MODEL', modelId, levelId })
      void fetchPanel(modelId, levelId, true)
    },
    [levelForModel, fetchPanel]
  )

  const selectLevel = useCallback(
    (levelId: number) => {
      dispatch({ type: 'SELECT_LEVEL', levelId })
      void fetchPanel(stateRef.current.activeModelId, levelId, true)
    },
    [fetchPanel]
  )

  const toggleFeatures = useCallback(() => dispatch({ type: 'TOGGLE_FEATURES' }), [])

  const prefetchModel = useCallback(
    (modelId: number) => {
      void fetchPanel(modelId, levelForModel(modelId), false)
    },
    [fetchPanel, levelForModel]
  )

  const prefetchLevel = useCallback(
    (levelId: number) => {
      void fetchPanel(stateRef.current.activeModelId, levelId, false)
    },
    [fetchPanel]
  )

  // Once the section nears the viewport, prefetch (on idle) the one-click-away
  // panels: the active model's other levels + each other model's first level.
  useEffect(() => {
    const el = panelsRef.current
    if (!el || !('IntersectionObserver' in window)) {
      return
    }
    let done = false
    const schedule = (cb: () => void) => {
      const w = window as IdleWindow
      if (typeof w.requestIdleCallback === 'function') {
        w.requestIdleCallback(cb, { timeout: 2000 })
      } else {
        setTimeout(cb, 300)
      }
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || done) {
          return
        }
        done = true
        observer.disconnect()
        schedule(() => {
          const { activeModelId, activeLevelId } = stateRef.current
          for (const lvl of levelsByModel[activeModelId] ?? []) {
            if (lvl !== activeLevelId) {
              void fetchPanel(activeModelId, lvl, false)
            }
          }
          for (const modelIdStr of Object.keys(levelsByModel)) {
            const modelId = Number(modelIdStr)
            if (modelId === activeModelId) {
              continue
            }
            const valid = levelsByModel[modelId] ?? []
            const lvl = valid.includes(activeLevelId ?? -1) ? activeLevelId : valid[0]
            void fetchPanel(modelId, lvl, false)
          }
        })
      },
      { rootMargin: '600px 0px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [levelsByModel, fetchPanel])

  return {
    activeModelId: state.activeModelId,
    activeLevelId: state.activeLevelId,
    expanded: state.expanded,
    panels: state.panels,
    tooltips: state.tooltips,
    loadingKey: state.loadingKey,
    panelsRef,
    selectModel,
    selectLevel,
    toggleFeatures,
    prefetchModel,
    prefetchLevel,
  }
}
